'use client';

import React, { useState } from 'react';

export type AreaId = 'italy' | 'japan' | 'rainbow_road' | 'california';

export interface AreaConfig {
  id: AreaId;
  name: string;
  subtitle: string;
  bgImage: string;
  bgColor: string;
  accentColor: string;
  glowColor: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  difficultyColor: string;
  locked: boolean;
  flag: string;
  tagline: string;
  levelNum: number;
  levelLabel: string;
}

export const AREAS: AreaConfig[] = [
  {
    id: 'italy',
    name: 'Italy',
    subtitle: 'Amalfi Coast',
    bgImage: '/assets/images/IMG_8032-1776974468024.jpeg',
    bgColor: '#1a0800',
    accentColor: '#ff6b35',
    glowColor: 'rgba(255,107,53,0.45)',
    difficulty: 'Beginner',
    difficultyColor: '#22c55e',
    locked: false,
    flag: '🇮🇹',
    tagline: 'Sun-soaked cliffside roads',
    levelNum: 1,
    levelLabel: 'Level 1',
  },
  {
    id: 'japan',
    name: 'Japan',
    subtitle: 'Mt Fuji',
    bgImage: '/assets/images/ChatGPT_Image_May_2_2026_12_15_28_PM-1777749407320.png',
    bgColor: '#0a001a',
    accentColor: '#e879f9',
    glowColor: 'rgba(232,121,249,0.45)',
    difficulty: 'Intermediate',
    difficultyColor: '#facc15',
    locked: false,
    flag: '🇯🇵',
    tagline: 'Misty mountain forest roads',
    levelNum: 2,
    levelLabel: 'Level 2',
  },
  {
    id: 'rainbow_road',
    name: 'Rainbow Road',
    subtitle: 'Sky Circuit',
    bgImage: '/assets/images/Screenshot_2026-05-02_at_12.15.42_PM-1777749439361.jpg',
    bgColor: '#0d0030',
    accentColor: '#a78bfa',
    glowColor: 'rgba(167,139,250,0.5)',
    difficulty: 'Advanced',
    difficultyColor: '#f97316',
    locked: false,
    flag: '🌈',
    tagline: 'Floating rainbow track in the sky',
    levelNum: 3,
    levelLabel: 'Level 3',
  },
  {
    id: 'california',
    name: 'California',
    subtitle: 'Pacific Coast',
    bgImage: '',
    bgColor: '#001a0a',
    accentColor: '#00e5ff',
    glowColor: 'rgba(0,229,255,0.5)',
    difficulty: 'Expert',
    difficultyColor: '#ef4444',
    locked: false,
    flag: '🌊',
    tagline: 'Wild coastal highways — maximum speed',
    levelNum: 4,
    levelLabel: 'Level 4',
  },
];

interface AreaSelectScreenProps {
  onSelect: (area: AreaConfig) => void;
}

const LEVEL_BADGE_STYLES: Record<number, { bg: string; border: string; text: string }> = {
  1: { bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.5)',   text: '#22c55e' },
  2: { bg: 'rgba(250,204,21,0.12)', border: 'rgba(250,204,21,0.5)',  text: '#facc15' },
  3: { bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.5)',  text: '#f97316' },
  4: { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.6)',   text: '#ef4444' },
};

export default function AreaSelectScreen({ onSelect }: AreaSelectScreenProps) {
  const [hoveredId, setHoveredId] = useState<AreaId | null>(null);
  const [selectedId, setSelectedId] = useState<AreaId | null>(null);

  const handleSelect = (area: AreaConfig) => {
    if (area.locked) return;
    setSelectedId(area.id);
    setTimeout(() => onSelect(area), 320);
  };

  return (
    <div
      className="relative w-screen h-screen overflow-hidden flex flex-col items-center justify-center"
      style={{
        background: 'radial-gradient(ellipse 120% 80% at 50% 0%, #1a0800 0%, #0d0d0f 55%, #000 100%)',
        fontFamily: 'DM Sans, sans-serif',
      }}
    >
      {/* Animated background grain */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
          opacity: 0.5,
        }}
      />

      {/* Top glow — shifts color based on hovered area */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          width: '70vw',
          height: '35vh',
          background: hoveredId
            ? `radial-gradient(ellipse at 50% 0%, ${AREAS.find(a => a.id === hoveredId)?.glowColor ?? 'rgba(255,107,53,0.18)'} 0%, transparent 70%)`
            : 'radial-gradient(ellipse at 50% 0%, rgba(255,107,53,0.18) 0%, transparent 70%)',
          transition: 'background 0.4s ease',
        }}
      />

      {/* Header */}
      <div className="relative z-10 flex flex-col items-center mb-6 md:mb-8 px-4 text-center">
        <div
          className="mb-2 text-xs font-bold tracking-[0.35em] uppercase"
          style={{ color: 'rgba(255,107,53,0.7)' }}
        >
          Choose Your Run
        </div>
        <h1
          className="text-4xl md:text-5xl font-black tracking-tight"
          style={{
            color: '#fff',
            textShadow: '0 0 40px rgba(255,107,53,0.4)',
            letterSpacing: '-0.02em',
          }}
        >
          Select Level
        </h1>
        <p
          className="mt-2 text-sm"
          style={{ color: 'rgba(255,255,255,0.38)' }}
        >
          4 levels — each one harder and wilder than the last
        </p>
      </div>

      {/* Area Cards Grid */}
      <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 px-4 w-full max-w-5xl">
        {AREAS.map((area) => {
          const isHovered = hoveredId === area.id;
          const isSelected = selectedId === area.id;
          const badge = LEVEL_BADGE_STYLES[area.levelNum];

          return (
            <button
              key={area.id}
              onClick={() => handleSelect(area)}
              onMouseEnter={() => setHoveredId(area.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                position: 'relative',
                borderRadius: '18px',
                overflow: 'hidden',
                border: isSelected
                  ? `2px solid ${area.accentColor}`
                  : isHovered
                  ? `2px solid ${area.accentColor}88`
                  : '2px solid rgba(255,255,255,0.07)',
                background: area.bgColor,
                cursor: 'pointer',
                transform: isSelected ? 'scale(0.96)' : isHovered ? 'scale(1.04)' : 'scale(1)',
                transition: 'transform 0.22s cubic-bezier(0.34,1.56,0.64,1), border-color 0.18s ease, box-shadow 0.22s ease',
                boxShadow: isHovered
                  ? `0 0 40px ${area.glowColor}, 0 8px 40px rgba(0,0,0,0.7)`
                  : isSelected
                  ? `0 0 24px ${area.glowColor}`
                  : '0 4px 20px rgba(0,0,0,0.5)',
                aspectRatio: '3/4',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
                padding: 0,
              }}
            >
              {/* Thumbnail / Background */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: area.bgImage ? `url(${area.bgImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: isHovered ? 0.6 : 0.35,
                  transition: 'opacity 0.25s ease',
                }}
              />

              {/* Gradient overlay */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, ${area.bgColor}bb 55%, ${area.bgColor}f0 100%)`,
                }}
              />

              {/* Accent glow on hover */}
              {isHovered && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(ellipse 90% 70% at 50% 110%, ${area.accentColor}28 0%, transparent 65%)`,
                  }}
                />
              )}

              {/* Top accent line */}
              <div
                className="absolute top-0 left-0 right-0"
                style={{
                  height: 3,
                  background: isHovered || isSelected
                    ? `linear-gradient(90deg, transparent, ${area.accentColor}, transparent)`
                    : `linear-gradient(90deg, transparent, ${area.accentColor}44, transparent)`,
                  transition: 'background 0.25s ease',
                }}
              />

              {/* Level number — top left */}
              <div
                className="absolute top-3 left-3 z-20"
                style={{
                  fontSize: '0.58rem',
                  fontWeight: 900,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: badge.text,
                  background: badge.bg,
                  border: `1px solid ${badge.border}`,
                  borderRadius: '6px',
                  padding: '3px 8px',
                }}
              >
                {area.levelLabel}
              </div>

              {/* Content */}
              <div
                className="relative z-10 flex flex-col justify-end h-full p-3 md:p-4"
              >
                {/* Flag + Difficulty badge */}
                <div className="flex items-center justify-between mb-2">
                  <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{area.flag}</span>
                  <span
                    style={{
                      fontSize: '0.58rem',
                      fontWeight: 800,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      color: area.difficultyColor,
                      background: `${area.difficultyColor}1a`,
                      border: `1px solid ${area.difficultyColor}55`,
                      borderRadius: '6px',
                      padding: '2px 7px',
                    }}
                  >
                    {area.difficulty}
                  </span>
                </div>

                {/* Name */}
                <div
                  style={{
                    fontSize: '1.15rem',
                    fontWeight: 900,
                    color: '#fff',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.1,
                    textShadow: isHovered ? `0 0 20px ${area.accentColor}88` : 'none',
                    transition: 'text-shadow 0.2s ease',
                  }}
                >
                  {area.name}
                </div>

                {/* Subtitle */}
                <div
                  style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: area.accentColor,
                    marginTop: 2,
                    letterSpacing: '0.04em',
                  }}
                >
                  {area.subtitle}
                </div>

                {/* Tagline */}
                <div
                  style={{
                    fontSize: '0.62rem',
                    color: 'rgba(255,255,255,0.42)',
                    marginTop: 4,
                    lineHeight: 1.4,
                  }}
                >
                  {area.tagline}
                </div>

                {/* Play button */}
                <div
                  style={{
                    marginTop: 10,
                    height: 34,
                    borderRadius: 9,
                    background: isHovered
                      ? `linear-gradient(135deg, ${area.accentColor} 0%, ${area.accentColor}cc 100%)`
                      : 'rgba(255,255,255,0.07)',
                    border: isHovered
                      ? 'none'
                      : `1px solid rgba(255,255,255,0.14)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: isHovered ? `0 4px 20px ${area.glowColor}` : 'none',
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: isHovered ? '#0d0d0f' : 'rgba(255,255,255,0.55)',
                      transition: 'color 0.18s ease',
                    }}
                  >
                    {isSelected ? 'Loading…' : 'Play Now'}
                  </span>
                  {isHovered && !isSelected && (
                    <span style={{ fontSize: '0.75rem', color: '#0d0d0f' }}>▶</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Bottom hint */}
      <div
        className="relative z-10 mt-6 md:mt-8 text-center px-4"
        style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem', letterSpacing: '0.06em', lineHeight: 1.7 }}
      >
        <span style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 700 }}>🎮 Controls:</span>
        {' '}
        <span style={{ color: '#00e5ff', fontWeight: 700 }}>← →</span> Steer
        {' · '}
        <span style={{ color: '#ffd60a', fontWeight: 700 }}>B</span> Jump / Big Air
        {' · '}
        <span style={{ color: '#ff9f43', fontWeight: 700 }}>N</span> Nosedive / Spin
        {' · '}
        <span style={{ color: '#a8e6cf', fontWeight: 700 }}>B + N together</span> = COMBO! 🔥
      </div>
    </div>
  );
}
